import {useState} from "react";
import {AddOn, useDigitalProductAddOnMutations} from "../../../features/digital-products";
import {ProductVariantsFormValues} from "../../../components/offering-form/products/variants/types";
import {performAddOnSave} from "./perform-add-on-save";

/**
 * Orchestrates persisting the add-ons form against the v2 API, shared by the
 * edit page and the create wizard. Thin wrapper over `performAddOnSave` (the
 * testable, React-free orchestration) that wires React Query mutations and
 * tracks a saving flag.
 *
 * The save may fan out to: DELETE(s) → PUT (existing groups) → POST (new
 * groups). Returns the resulting groups (with server ids) so callers can
 * capture them (e.g. the create wizard, to make Back→Submit idempotent).
 */
export function useSaveAddOns(productId: number) {
  const {createAddOnsAsync, updateAddOnsAsync, deleteAddOnAsync, deleteAddOnOptionAsync} =
    useDigitalProductAddOnMutations(productId);
  const [isSaving, setIsSaving] = useState(false);

  async function saveAddOns(
    snapshot: AddOn[],
    values: ProductVariantsFormValues
  ): Promise<AddOn[]> {
    setIsSaving(true);
    try {
      return await performAddOnSave(snapshot, values, {
        createAddOns: (input) => createAddOnsAsync(input),
        updateAddOns: (input) => updateAddOnsAsync(input),
        deleteAddOn: (addOnId) => deleteAddOnAsync({addOnId}),
        deleteAddOnOption: (addOnId, optionId) =>
          deleteAddOnOptionAsync({addOnId, optionId}),
      });
    } finally {
      setIsSaving(false);
    }
  }

  return {saveAddOns, isSaving};
}
